import Badge from '../../../../components/Badge'
import Modal from '../../../../components/Modal'
import DropdownMenu from '../../../../components/DropdownMenu'
import { memo, useState, useEffect, useContext, useCallback, useMemo, Fragment } from 'react'
import WorkCenterForm from '../../WorkCenterForm'

import { OrderStatusCode, OrderStatusTheme, OrderStatusForAdminDisplay, OrderType, Tags, OperationTime, OperationTimeQC } from '../../../../constants/Enum'
import { DgtPdOrderOperation, DgtPdOrderOperationLog, DgtPdProductionOrder } from '../../../../models'
import { DataStore, SortDirection } from '@aws-amplify/datastore'
import { Auth, API } from 'aws-amplify'
import UserContext from '../../../../contexts/UserContext'
import SyncExpressionContext from '../../../../contexts/SyncExpressionContext'
import RecordTimeTypeContext from '../../../../contexts/RecordTimeTypeContext'
import { syncQueriesReady, removeSync } from '../../../../sync/DataStoreSync'
import OrderConfirm from '../../OrderConfirm'
import ReSyncOrder from '../../../Dialogs/ReSyncOrder'
import { mergeUpdateToOriginWorkCenters, buildConfirmation, countEditWc, groupByMachine, validateRtOtLogs } from '../logic'
import EditWorkCenter from '../../EditWorkCenter'
import { Switch } from '@headlessui/react'
import DataGrid, { CalculatedColumn, RowsChangeData } from 'react-data-grid'
import { OperationLogsColumns, SummaryLogRow } from '../../../../constants/Column'
import CheckboxFormatter from '../../../../formatters/CheckboxFormatter'
import { 
  updateLogSplit,
  updateConfirmationLogs,
  updateLogShift,
  updateTags,
  updateLogMch,
  updateLogBatchLot,
  updateCountEditWorkCenterDirectly
} from '../../../../apis'
import { ceil2Decimal, formatDateISOToTh } from '../../../../utilities/Format'
import { FiXCircle } from 'react-icons/fi'
import { RiUserUnfollowLine } from 'react-icons/ri'

const WorkCenterConfirm = ({ order, workCenters, setOrder, pleaseRefresh, operationRefresh }) => {
  const firstOpID = workCenters.length > 0 ? workCenters[0].operationNo : ''
  const [currentOperationNo, setCurrentOperationNo] = useState(firstOpID)

  const [editLogId, setEditLogId] = useState('')

  const [isEdit, setIsEdit] = useState(false)

  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const [showConfirmDeleteOp, setShowConfirmDeleteOp] = useState(false)

  const [showReSyncDialog, setShowReSyncDialog] = useState(false)

  const [showConfirmSave, setShowConfirmSave] = useState(false)

  const [logIdForDelete, setLogIdForDelete] = useState('')

  const [opIdForDelete, setOpIdForDelete] = useState('')

  const [operationLogs, setOperationLogs] = useState([])

  const [updatedOperations, setUpdatedOperations] = useState(null)

  const [listsOpFromSAP, setListsOpFromSAP] = useState(null)
  
  const [latestOpFromSAP, setLatestOpFromSAP] = useState(null)

  const [isLoading, setLoading] = useState(false);

  const [errorApi, setErrorApi] = useState(null);

  const [showEditWc, setShowEditWc] = useState(false)

  const [enablePartial, setEnablePartial] = useState(false);

  const [sortColumns, setSortColumns] = useState([]);

  const [tempRemoveOpTitle, setTempRemoveOpTitle] = useState('');

  const [ invalidRtOt , setInvalidRtOt] = useState(false)

  const { user, readOnly } = useContext(UserContext)
  const { setSyncOperationID } = useContext(SyncExpressionContext)
  const { currentTimeType, setCurrentTimeType } = useContext(RecordTimeTypeContext)

  const fetchOperationLogs = async (filter) => {
    try {
      const operationLogs = await DataStore.query(DgtPdOrderOperationLog, filter, {
        sort: s => s.startedTime(SortDirection.ASCENDING)
      })
      setOperationLogs(operationLogs)
      const isEnablePartial = operationLogs.reduce((prev, cur) => prev + (cur.confirmationId ? 1 : 0), 0) > 0
      if (isEnablePartial) setEnablePartial(true)
    } catch (error) {
      console.log(error)
    }
  }

  const onWorkCenterSelected = (key) => {
    setCurrentOperationNo(key)
  }

  const onEditRecord = useCallback((logId) => {
    setEditLogId(logId)
    setIsEdit(true)
    setCurrentTimeType('Edit')
  }, [ setCurrentTimeType ])

  const onDeleteRecord = useCallback((logId) => {
    setShowConfirmDelete(true)
    setLogIdForDelete(logId)
  }, [])

  const onConfirmDelete = async () => {
    try {
      const logForDelete = await DataStore.query(DgtPdOrderOperationLog, logIdForDelete);
      DataStore.delete(logForDelete);
    } catch (error) {
      console.log(error)
    }
    setShowConfirmDelete(false)
    setLogIdForDelete('')
    const cloneLogs = [...operationLogs]
    const idxForRemove = cloneLogs.findIndex(lg => lg.id === logIdForDelete)
    cloneLogs.splice(idxForRemove, 1);
    setOperationLogs(cloneLogs)
  }

  const onConfirmDeleteOp = async () => {
    try {
      const opForDelete = await DataStore.query(DgtPdOrderOperation, opIdForDelete);
      DataStore.delete(opForDelete);
      setCurrentOperationNo(firstOpID)
      operationRefresh()
    } catch (error) {
      console.log(error)
    }
    setShowConfirmDeleteOp(false)
    setOpIdForDelete('')
  }

  const onSubmitConfirmOperation = async (id) => {
    setLoading(true)
    setErrorApi(null)
    try {
      const selectedLogs = enablePartial ? operationLogs.filter(log => !log.confirmationId && log.confirmedDate) : operationLogs
      const confirmationPayload = buildConfirmation(order.id, currentOperationNo, selectedLogs)

      const session = await Auth.currentSession()
      const idToken = session.idToken.jwtToken
      const postInit = {
        body: {
          ...confirmationPayload,
          isPartial: enablePartial
        },
        headers: { "Content-Type": "application/json", "Authorization": idToken }
      }
      const apiName = process.env.REACT_APP_REST_API_NAME
      const posted = await API.post(apiName, `/sap/order/confirmation`, postInit)
      const { confirmationId } = posted

      if (enablePartial) {
        const now = new Date()
        const updates = await updateConfirmationLogs(
          selectedLogs, confirmationId, now.toISOString(), user?.name || user?.username)
        const latestSelectedLogs = updates.map(upd => upd.value)
        const mergeLogs = operationLogs.map(log => {
          const mergeLog = latestSelectedLogs.find(l => l.id === log.id)
          return mergeLog ? mergeLog : log
        })

        setOperationLogs(mergeLogs)

        const notConfirmedLogs = mergeLogs.filter(log => !log.confirmationId)

        if (notConfirmedLogs.length === 0 && order.statusFromApp === OrderStatusCode.DRAFTED) {
          const originalOperation = await DataStore.query(DgtPdOrderOperation, id)
          if (originalOperation) {
            const updateOperation = DgtPdOrderOperation.copyOf(originalOperation, updated => {
              updated.status = OrderStatusCode.CONFIRMED
            })
            await DataStore.save(updateOperation)

            const newUpdateOperations = {
              ...updatedOperations,
              [originalOperation.operationNo]: updateOperation
            }
            setUpdatedOperations(newUpdateOperations)
            pleaseRefresh()
          }
        }
        await addTagsPartial()
      } else {
        const originalOperation = await DataStore.query(DgtPdOrderOperation, id)
        if (originalOperation) {
          const now = new Date()
          const updateOperation = DgtPdOrderOperation.copyOf(originalOperation, updated => {
            updated.status = OrderStatusCode.CONFIRMED
            updated.confirmedDate = now.toISOString()
            updated.confirmedBy = user?.name || user?.username
            updated.confirmationId = confirmationId
          })
          await DataStore.save(updateOperation)

          const newUpdateOperations = {
            ...updatedOperations,
            [originalOperation.operationNo]: updateOperation
          }
          setUpdatedOperations(newUpdateOperations)

          pleaseRefresh()
        }
      }
      setLoading(false)
      setShowConfirmSave(false)
    } catch (error) {
      setLoading(false)
      console.log(error)
      if (error.response.data) {
         // Individual capacity does not exist
        if (error.response.data.message) {
          if (error.response.data.message.includes('Individual capacity')) {
            setErrorApi('ไม่สามารถ Confirm ได้ถ้ายังไม่ได้กำหนด Split')
          } else {
            setErrorApi(error.response.data.message)
          }
        } else {
          setErrorApi('ไม่สามารถ Confirm Order ได้กรุณาแจ้งผู้ดูแลระบบ')
        }
      } else {
        setErrorApi(`API do not available for ${process.env.REACT_APP_ENV}`)
      }
    }
  }

  const addTagsPartial = async () => {
    try {
      const updateTag = await updateTags(order.id, Tags.PARTIAL)
      return updateTag
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    setSyncOperationID(currentOperationNo)
    const listener1 = syncQueriesReady(fetchOperationLogs, log => log.productionOrderId('eq', order.id).operationNo('eq', currentOperationNo))
    return () => {
      removeSync(listener1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, currentOperationNo])

  const onBackFromRecord = () => {
    setCurrentTimeType('')
    setIsEdit(false)
    setEditLogId('')
    setSyncOperationID(currentOperation.operationNo)
    fetchOperationLogs(log => log.productionOrderId('eq', order.id).operationNo('eq', currentOperation.operationNo))
  }

  const onSyncCompleted = (latestOps) => {
    setShowReSyncDialog(false)
    const latestOp = latestOps.find(lop => lop['ACTIVITY_NUM'] === currentOperationNo)
    setLatestOpFromSAP(latestOp)
    setListsOpFromSAP(latestOps)
    setShowConfirmSave(true)
  }
  
  const onEditWorkCenterCompleted = async (selectedWorkCenter, newWc) => {
    if (!newWc) {
      setShowEditWc(false)
      return
    }
    try {
      const originalOperation = await DataStore.query(DgtPdOrderOperation, selectedWorkCenter.id)
      if (originalOperation) {
        const updateOperation = DgtPdOrderOperation.copyOf(originalOperation, updated => {
          updated.actualWorkCenterId = newWc.id
          updated.actualWorkCenterDescription = newWc.description
        })
        await DataStore.save(updateOperation)

        const updatedOps = {
          ...updatedOperations,
          [originalOperation.operationNo]: {
            ...updateOperation,
            isRequiredManHour: newWc.isRequiredManHour,
            isRequiredBatchLotNo: newWc.isRequiredBatchLotNo !== null ? newWc.isRequiredBatchLotNo : true,
            isRequiredFGInspect: newWc.isRequiredFGInspect !== null ? newWc.isRequiredFGInspect : false
          }
        }
        await updateCountEditWorkCenter(updatedOps)

        setUpdatedOperations(updatedOps)
      }
    } catch (error) {
      console.log(error)
    }
    setShowEditWc(false)
  }

  const updateCountEditWorkCenter = async (updatedOps) => {
    const mergedWorkCenters = mergeUpdateToOriginWorkCenters(workCenters, updatedOps)

    const originalOrder = await DataStore.query(DgtPdProductionOrder, order.id)
    if (originalOrder) {
      const updateOrder = DgtPdProductionOrder.copyOf(originalOrder, updated => {
        updated.countEditWorkCenter = countEditWc(mergedWorkCenters)
      })
      await DataStore.save(updateOrder)
      setOrder(updateOrder)
    } else {
      const updatedOrder = await updateCountEditWorkCenterDirectly(order.id, order._version, countEditWc(mergedWorkCenters))
      console.log('update to server', updatedOrder)
      setOrder(updatedOrder)
    }
  }

  const onAddMoreLog = (timeType) => {
    setIsEdit(true)
    setCurrentTimeType(timeType)
  }

  const onRowClick = (
    row: DgtPdOrderOperationLog,
    column: CalculatedColumn<DgtPdOrderOperationLog>) => {
    if (row.timeType === OperationTimeQC.INSPECT_BULK)  return

    if (column.key === 'edit') onEditRecord(row.id)
    if (column.key === 'delete') onDeleteRecord(row.id)
  }

  const onRowsChange = (
    rows: DgtPdOrderOperationLog[],
    changedInfo: RowsChangeData<DgtPdOrderOperationLog>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return

    const changedRow = rows[changedRowIdx]

    if(changedInfo.column){
      if (changedInfo.column.key === 'split') {
        updateLogSplit(changedRow.id, changedRow.split || 1)
      } else if (changedInfo.column.key === 'shift') {
        updateLogShift(changedRow.id, changedRow.shift || "1")
      } else if (changedInfo.column.key === 'mch') {
        updateLogMch(changedRow.id, changedRow.mch || '')
      } else if (changedInfo.column.key === 'confirmedDate') {
        // do nothing
      } else if (changedInfo.column.key === 'batchNo') {
        updateLogBatchLot(changedRow.id, changedRow.batchNo)
      }
    }

    setOperationLogs(rows)
  }

  const currentOperation = useMemo(() => {
    const originWc = workCenters.find(wc => wc.operationNo === currentOperationNo)
    if (updatedOperations) {
      const updatedWc = updatedOperations[currentOperationNo]
      return { ...originWc, ...updatedWc }
    }
    return originWc
  }, [ workCenters, currentOperationNo, updatedOperations ])

  const mergedWorkCenters = mergeUpdateToOriginWorkCenters(workCenters, updatedOperations)

  const originWcFromSap = mergedWorkCenters.filter(wc => wc.active && (!wc.createdBySystem || wc.createdBySystem === 'SAP'))
  const mustDeleteWcFromSap = mergedWorkCenters.filter(wc => !wc.active && (!wc.createdBySystem || wc.createdBySystem === 'SAP'))
  const mustAddWcToSap = mergedWorkCenters.filter(wc => wc.active && wc.createdBySystem === 'APP')

  const isOperationConfirmed = currentOperation.status && currentOperation.status === OrderStatusCode.CONFIRMED

  let columns = Array.from(OperationLogsColumns)
  columns.splice(order.materialType === OrderType.BULK ? 14 : 13, 1)
  if (!enablePartial) {
    columns.splice(0, 1)
    columns.splice(3, 1)
    columns.splice(columns.length - 4, 2)
  }

  if (readOnly) {
    columns.splice(columns.length - 2, 2)
  }

  const sortedLogs = useMemo(() => {
    if (sortColumns.length === 0) return operationLogs;

    return [...operationLogs].sort((a, b) => {
      const sort = sortColumns[0]
      let compRes = 0
      if (sort.columnKey === 'mch') compRes = a.mch.localeCompare(b.mch)
      if (sort.columnKey === 'split') compRes = (a.split || 0) - (b.split || 0)

      return sort.direction === 'ASC' ? compRes : -compRes;
    });
  }, [ operationLogs, sortColumns ]);

  const summaryLogRows = useMemo(() => {
    const summaryRow : SummaryLogRow = { 
      totalYield: ceil2Decimal(operationLogs.reduce((prev, { yield : y }) => prev + y, 0)),
    }
    return [ summaryRow ];
  }, [ operationLogs ]);

  const onPressConfirm = () => {
    if (currentOperation.isRequiredManHour ||  Number(currentOperationNo) >= 20) {
      // action : validate
      const selectedLogs = enablePartial ? operationLogs.filter(log => !log.confirmationId && log.confirmedDate) : operationLogs
      if (validateRtOtLogs(selectedLogs, false)) {
        setShowReSyncDialog(true)
        setInvalidRtOt(false)
      } else {
        setInvalidRtOt(true)
      }
    } else {
      // action : no validate (isRequiredMh = false and op = 0010)
      setShowReSyncDialog(true)
      setInvalidRtOt(false)
    }
  }

  return <>{isEdit ? <WorkCenterForm workCenter={currentOperation} 
    timeTitle={currentTimeType}
    onBack={onBackFromRecord}
    logId={editLogId}
    isEditTime={true}
    isRequiredBatchLotNo={currentOperation.isRequiredBatchLotNo}
    /> :
    <div className='pb-3 space-y-3'>
      {/* left side menu */}
      <div className=''>
        <h6 className='mb-3 '>รายการ Operation</h6>
        <div className="list-group-horizontal">
        {originWcFromSap.map(wc =>
          <button key={wc.operationNo}
            className={`list-item-horizontal ${currentOperationNo === wc.operationNo ? ' bg-blue-100 border-blue-600':''}`}
            onClick={() => onWorkCenterSelected(wc.operationNo)}
          >
            <div className="flex items-center justify-center gap-2">
              <h6><Badge variant={OrderStatusTheme[wc.status]}>{OrderStatusForAdminDisplay[wc.status]}</Badge></h6>
              <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`Op No: ${wc.operationNo} `}</h6>
              <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`WC: ${wc.actualWorkCenterDescription ? wc.actualWorkCenterDescription : wc.workCenterDescription} (${wc.actualWorkCenterId ? wc.actualWorkCenterId : wc.workCenterId})`}</h6>
            </div>
          </button>
        )}
        {mustAddWcToSap.map(wc =>
          <Fragment key={`card-op-new-${wc.operationNo}`}>
            <button key={wc.operationNo}
              className={`list-item-horizontal ${currentOperationNo === wc.operationNo ? ' bg-blue-100 border-blue-600':''}`}
              onClick={() => onWorkCenterSelected(wc.operationNo)}
            >
              <div className="flex items-center justify-center gap-2">
                <h6><Badge variant="new">ใหม่</Badge> {OrderStatusForAdminDisplay[wc.status] ? <Badge variant={OrderStatusTheme[wc.status]}>{OrderStatusForAdminDisplay[wc.status]}</Badge>: null}</h6>
                <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`Op No: ${wc.operationNo} `}</h6>
                <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`WC: ${wc.actualWorkCenterDescription ? wc.actualWorkCenterDescription : wc.workCenterDescription} (${wc.actualWorkCenterId ? wc.actualWorkCenterId : wc.workCenterId})`}</h6>
              </div>
            </button>
            <div className={`text-red-600 -ml-5 -mt-3 cursor-pointer`}
              onClick={()=>{
                const tempTextOpTitle = `Op No: ${wc.operationNo} WC: ${wc.actualWorkCenterDescription ? wc.actualWorkCenterDescription : wc.workCenterDescription} (${wc.actualWorkCenterId ? wc.actualWorkCenterId : wc.workCenterId})`
                  setTempRemoveOpTitle(tempTextOpTitle)
                  setOpIdForDelete(wc.id)
                  setShowConfirmDeleteOp(true)
                }}>
              <FiXCircle size={25}/>
            </div>
          </Fragment>
          )}
        {mustDeleteWcFromSap.map(wc =>
          <button key={wc.operationNo}
            className={`list-item-horizontal ${currentOperationNo === wc.operationNo ? ' bg-blue-100 border-blue-600':''}`}
            onClick={() => onWorkCenterSelected(wc.operationNo)}
          >
            <div className="flex items-center justify-center gap-2">
              <h6><Badge variant="danger">ปิด</Badge></h6>
              <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`Op No: ${wc.operationNo} `}</h6>
              <h6 className={`${currentOperationNo === wc.operationNo ? ' text-blue-600':' text-gray-600'}`}>{`WC: ${wc.actualWorkCenterDescription ? wc.actualWorkCenterDescription : wc.workCenterDescription} (${wc.actualWorkCenterId ? wc.actualWorkCenterId : wc.workCenterId})`}</h6>
            </div>
          </button>
        )}
        </div>
      </div>
      {/* right side menu */}
      <div className={`${workCenters.length > 0 ? '':'hidden'}`}>
        <div className='grid grid-cols-4 gap-2 mb-2 lg:grid-cols-8'>
          <div>
            <small className='font-semibold text-muted'>Operation</small>
            <h6>{currentOperation.operationNo}</h6>
          </div>
          <div className='col-span-2'>
            <div className='flex items-center gap-2'>
              <small className='font-semibold text-muted'>WorkCenter</small>
              {readOnly ? null : <h6 className="mt-1 text-sm work-center-edit" onClick={() => setShowEditWc(true)}>แก้ไข</h6>}
            </div>
            <h6>{currentOperation.actualWorkCenterId && currentOperation.actualWorkCenterId !== currentOperation.workCenterId ?
              <span className='font-bold text-green-700'><Badge variant="new">ใหม่</Badge> {`${currentOperation.actualWorkCenterId} - ${currentOperation.actualWorkCenterDescription}`}</span> : null} {<span className={currentOperation.actualWorkCenterId && currentOperation.actualWorkCenterId !== currentOperation.workCenterId ? 'line-through' : ''}>{`${currentOperation.actualWorkCenterId && currentOperation.actualWorkCenterId !== currentOperation.workCenterId ? '(':''}${currentOperation.workCenterId} - ${currentOperation.workCenterDescription}${currentOperation.actualWorkCenterId && currentOperation.actualWorkCenterId !== currentOperation.workCenterId ? ')':''}`}</span>}
            </h6>
          </div>
          <div>
            <small className='font-semibold text-muted'>Machine</small>
            <h6>{groupByMachine(operationLogs).join(',')}</h6>
          </div>
          <div className='col-span-2'>
            <small className='font-semibold text-muted'>Confirm Info</small>
            <div className='flex flex-col'>
              <small className={`${isOperationConfirmed ? '':'hidden'} text-green-700`}>
                No: {currentOperation.confirmationId ? currentOperation.confirmationId : (operationLogs.length > 0 ? operationLogs[0].confirmationId : '')}
              </small>
              <small className={`${isOperationConfirmed ? '':'hidden'} text-green-700 break-all`}>
                By: {currentOperation.confirmedBy ? currentOperation.confirmedBy : (operationLogs.length > 0 ? `${operationLogs[0].confirmedBy} (${formatDateISOToTh(operationLogs[0].confirmedDate)})` : '')}
              </small>
            </div>
          </div>
          <div className='flex flex-col items-end justify-end col-span-2'>
            <div>
              <button
                className={`${currentOperation.active ? '':'hidden'} btn-success mr-2`}
                onClick={onPressConfirm}
                disabled={readOnly}>
                {order.statusFromApp === OrderStatusCode.CONFIRMED ? 'ยืนยันอีกครั้ง':'กดเพื่อยืนยัน'}
              </button>
              <DropdownMenu
                title='เพิ่ม'
                menus={Object.values(OperationTime).map(t => ({ timeType: t, active: true }))}
                classNameBtn='btn-outline-primary'
                onMenuSelected={onAddMoreLog}
                disabled={readOnly}
              />
            </div>
            {invalidRtOt ? <h6 className='flex justify-end gap-1 text-sm text-center text-red-500'><RiUserUnfollowLine size={20} />กรอกคน RT/OT ไม่ครบ</h6> : null}
          </div>
        </div>
        <div className='mb-5 overflow-x-auto'>
          <Switch.Group>
            <>
              <Switch
                checked={enablePartial}
                onChange={setEnablePartial}
                disabled={readOnly}
                className={`${
                  enablePartial ? 'bg-green-600' : 'bg-gray-200'
                } my-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
              >
                <span
                  className={`${
                    enablePartial ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <Switch.Label className="ml-2 work-center-edit">เปิด Confirm บางส่วน / หลาย Split</Switch.Label>
            </>
          </Switch.Group>
          <DataGrid
            style={{ height: "100%", contain: "none" }}
            columns={columns}
            rows={sortedLogs}
            onRowClick={onRowClick}
            onRowsChange={onRowsChange}
            rowKeyGetter={row => row.id}
            components={{ checkboxFormatter: CheckboxFormatter }}
            rowClass={row => row.confirmedDate ? ' bg-green-100' : ''}
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            summaryRows={summaryLogRows}
            defaultColumnOptions={{ resizable: true }}
          />
        </div>
      </div>
    </div>}
    <Modal
      show={showConfirmDelete}
      onHide={() => setShowConfirmDelete(false)}
      title='ลบข้อมูลที่เคยบันทึก'
      body='ผู้ร่วมงานยืนยันที่จะลบข้อมูล ?'
      cancelAction={() => setShowConfirmDelete(false)}
      cancelText='ยกเลิก'
      okAction={onConfirmDelete}
      okText='ยืนยัน'
      okTheme='danger'
    />
    <Modal
      show={showConfirmDeleteOp}
      onHide={() => setShowConfirmDeleteOp(false)}
      title={tempRemoveOpTitle}
      body='ผู้ร่วมงานยืนยันที่จะลบข้อมูล'
      cancelAction={() => setShowConfirmDeleteOp(false)}
      cancelText='ยกเลิก'
      okAction={onConfirmDeleteOp}
      okText='ยืนยัน'
      okTheme='danger'
    />
    <ReSyncOrder show={showReSyncDialog} onHide={() => setShowReSyncDialog(false)} orderId={order.id} onSyncCompleted={onSyncCompleted}/>
    <OrderConfirm show={showConfirmSave} onHide={() => {
      setErrorApi(null)
      setShowConfirmSave(false)
    }}
      latestOp={latestOpFromSAP} operationNo={currentOperationNo} actualWorkId={currentOperation.actualWorkCenterId || currentOperation.workCenterId}
      listsOpFromSAP={listsOpFromSAP}
      onSubmitConfirm={onSubmitConfirmOperation}
      id={currentOperation.id} isLoading={isLoading} errorApi={errorApi}
      isPartial={enablePartial} checkedLogs={operationLogs.filter(log => !log.confirmationId && log.confirmedDate)}
    />
    <EditWorkCenter currentWorkCenter={currentOperation} show={showEditWc} onHide={onEditWorkCenterCompleted}
      division={order.division} orderType={order.materialType}/>
  </>
}

export default memo(WorkCenterConfirm)